import { useEffect, useRef, useState } from "react";

interface Landmark {
  id: string;
  name: string;
  desc: string;
  top: string;
  left: string;
  detail: string;
  image: string;
}

const landmarks: Landmark[] = [
  {
    id: "gulangyu",
    name: "鼓浪屿",
    desc: "琴岛风情",
    top: "38%",
    left: "28%",
    detail: "鼓浪屿，别名琴岛，是厦门著名的旅游胜地。岛上建筑风格多样，有闽南传统民居、西洋别墅，被誉为「万国建筑博览会」。漫步其间，钢琴声悠扬，海风轻拂，令人心旷神怡。",
    image: "https://public.readdy.ai/ai/img_res/e5fa2cc0aad2fe8d6bb96c2c0302e344.jpg",
  },
  {
    id: "huandao",
    name: "环岛路",
    desc: "最美海岸线",
    top: "55%",
    left: "62%",
    detail: "环岛路全长约43公里，沿厦门岛东南海岸蜿蜒而行，被誉为「中国最美的滨海公路」。骑行或漫步其间，碧海蓝天、椰林沙滩尽收眼底，是厦门最具代表性的城市名片。",
    image: "https://readdy.ai/api/search-image?query=Xiamen%20Huandao%20coastal%20road%20scenic%20view%2C%20winding%20road%20along%20turquoise%20blue%20sea%2C%20tall%20palm%20trees%20lining%20both%20sides%2C%20white%20sandy%20beach%2C%20cyclists%20and%20pedestrians%20enjoying%20the%20path%2C%20golden%20hour%20warm%20light%2C%20clear%20sky%2C%20beautiful%20tropical%20coastal%20scenery%2C%20wide%20angle%20photography&width=600&height=400&seq=huandao_detail_001&orientation=landscape",
  },
  {
    id: "nanputuo",
    name: "南普陀寺",
    desc: "千年古刹",
    top: "25%",
    left: "72%",
    detail: "南普陀寺始建于唐末五代，依山面海，香火鼎盛。寺内古木参天，殿宇巍峨，是闽南地区著名的佛教圣地，也是厦门重要的文化地标之一。",
    image: "https://readdy.ai/api/search-image?query=Nanputuo%20Temple%20Xiamen%20Buddhist%20temple%20complex%2C%20traditional%20Chinese%20architecture%20with%20curved%20golden%20rooftops%2C%20red%20pillars%2C%20incense%20smoke%20rising%2C%20ancient%20stone%20steps%2C%20lush%20green%20mountain%20backdrop%2C%20devotees%20praying%2C%20serene%20spiritual%20atmosphere%2C%20detailed%20temple%20photography%2C%20warm%20natural%20light&width=600&height=400&seq=nanputuo_detail_001&orientation=landscape",
  },
];

interface SceneryPageProps {
  visible: boolean;
  onNext: () => void;
  onPrev: () => void;
}

export default function SceneryPage({ visible, onNext, onPrev }: SceneryPageProps) {
  const [activeLandmark, setActiveLandmark] = useState<Landmark | null>(null);
  const [entered, setEntered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setEntered(true), 100);
      return () => clearTimeout(t);
    } else {
      setEntered(false);
      setActiveLandmark(null);
    }
  }, [visible]);

  const handleLandmarkClick = (lm: Landmark) => {
    setActiveLandmark(activeLandmark?.id === lm.id ? null : lm);
  };

  // Compute popup position clamped to screen
  const getPopupStyle = (lm: Landmark): React.CSSProperties => {
    const topVal = parseFloat(lm.top);
    const leftVal = parseFloat(lm.left);
    return {
      top: `calc(${Math.min(topVal + 8, 55)}% + 40px)`,
      left: `clamp(8px, calc(${leftVal}% - 80px), calc(100% - 296px))`,
    };
  };

  return (
    <div ref={containerRef} className="relative w-full h-screen overflow-hidden flex">
      {/* Background */}
      <div className="relative w-full h-full">
        <img
          src="https://readdy.ai/api/search-image?query=Xiamen%20Gulangyu%20island%20aerial%20view%20with%20Huandao%20coastal%20road%2C%20turquoise%20sea%20water%2C%20lush%20tropical%20vegetation%2C%20colonial%20architecture%20buildings%2C%20white%20sandy%20beach%2C%20clear%20blue%20sky%20with%20fluffy%20clouds%2C%20warm%20golden%20afternoon%20light%2C%20panoramic%20bird%20eye%20view%2C%20vibrant%20colors%2C%20ultra%20detailed%2C%20cinematic%20photography&width=1920&height=1080&seq=scenery001&orientation=landscape"
          alt="厦门山海风光"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

        {/* Landmark Pins */}
        {landmarks.map((lm) => (
          <div
            key={lm.id}
            className="absolute z-20 cursor-pointer"
            style={{ top: lm.top, left: lm.left }}
            onClick={() => handleLandmarkClick(lm)}
          >
            <div className="relative flex items-center justify-center">
              <div className="absolute w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/30 animate-ping" />
              <div className="relative w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full bg-white shadow-lg">
                <i className="ri-map-pin-2-fill text-cyan-600 text-xs md:text-sm" />
              </div>
            </div>
            <div className="mt-1 text-center">
              <span className="text-white text-xs font-medium bg-black/40 backdrop-blur-sm px-1.5 md:px-2 py-0.5 rounded-full whitespace-nowrap">
                {lm.name}
              </span>
            </div>
          </div>
        ))}

        {/* Landmark Detail Popup — clamped position */}
        {activeLandmark && (
          <div
            className="absolute z-30 bg-white/95 backdrop-blur-md rounded-2xl overflow-hidden shadow-xl"
            style={{ ...getPopupStyle(activeLandmark), width: "min(288px, calc(100vw - 16px))" }}
          >
            <div className="w-full h-28 md:h-36 overflow-hidden">
              <img
                src={activeLandmark.image}
                alt={activeLandmark.name}
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="p-3 md:p-4">
              <div className="flex items-start justify-between mb-1.5">
                <div>
                  <h3 className="text-gray-900 font-semibold text-sm md:text-base">{activeLandmark.name}</h3>
                  <span className="text-cyan-600 text-xs">{activeLandmark.desc}</span>
                </div>
                <button
                  onClick={() => setActiveLandmark(null)}
                  className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-gray-500 text-sm" />
                </button>
              </div>
              <p className="text-gray-600 text-xs leading-relaxed">{activeLandmark.detail}</p>
            </div>
          </div>
        )}

        {/* Content Overlay — bottom left */}
        <div
          className={`absolute left-5 md:left-16 bottom-16 md:bottom-20 z-20 transition-all duration-1000 ${entered ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
        >
          <div className="inline-flex items-center gap-2 border border-white/40 rounded-full px-3 py-1 mb-3 md:mb-4">
            <span className="text-white/70 text-xs tracking-widest">02 / SCENERY</span>
          </div>
          <h2
            className="text-4xl md:text-6xl font-light text-white mb-2 md:mb-3 leading-tight"
            style={{ fontFamily: "'Noto Serif SC', serif" }}
          >
            山海之美
          </h2>
          <p className="text-white/80 text-xs md:text-base font-light leading-relaxed max-w-xs">
            碧海蓝天，琴音绕岛
            <br />
            一步一景，美不胜收
          </p>
          <div className="flex items-center gap-2 mt-3 md:mt-4 text-white/50 text-xs">
            <i className="ri-map-pin-line" />
            <span>点击地标，探索故事</span>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={onPrev}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={onNext}
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* Top Nav */}
      <nav className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 md:px-8 py-4 md:py-5">
        <img
          src="https://public.readdy.ai/ai/img_res/87bc98b4-e616-459b-81b0-cc0db54579b8.png"
          alt="Logo"
          className="h-8 md:h-9 w-auto object-contain"
        />
        <div className="hidden md:flex items-center gap-6">
          {["山海", "人文", "美食", "活力"].map((item) => (
            <span key={item} className="text-white/80 text-xs tracking-widest cursor-pointer hover:text-white transition-colors whitespace-nowrap">
              {item}
            </span>
          ))}
        </div>
      </nav>

      {/* Page indicator */}
      <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`rounded-full transition-all duration-300 ${i === 1 ? "w-6 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/40"}`} />
        ))}
      </div>
    </div>
  );
}
